import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useParams } from "react-router-dom";
// import styles from "./CoachClosingFeedback.module.scss";
import { COACH_MANUAL_FEEDBACK } from "../../../config/endpoints";
import TagHelper from "../../../helpers/PostHelpers/TagHelper";

const CoachClosingFeedback = ({ readOnly = false, coachManualFeedback = null, videoTagList = [] }) => {
  const [closingFeedback, setClosingFeedback] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState("idle"); // 'idle' | 'submitting' | 'submitted'
  const { postId } = useParams();
  const jwtToken = localStorage.getItem("BQS_TOKEN");
  const [templateCollections, setTemplateCollections] = useState({ grips: [], airtoss: [] });
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [selectedTemplateGroup, setSelectedTemplateGroup] = useState("");
  const [selectedTemplateComment, setSelectedTemplateComment] = useState("");

  // Cache ref to prevent redundant markdown fetches
  const markdownCacheRef = React.useRef({});

  const normalizedTags = useMemo(() => {
    const extracted = TagHelper.extractTagNamesFromObjects(videoTagList);
    return extracted.map((tag) => (tag || "").toLowerCase());
  }, [videoTagList]);

  const parseMarkdownSections = (content) => {
    const lines = content.split("\n");
    const sections = [];
    let currentSection = null;

    lines.forEach((rawLine) => {
      const line = rawLine.trim();
      if (line.startsWith("####")) {
        const name = line.replace(/^####\s*/, "").replace(/:+$/, "").trim();
        currentSection = { name, comments: [] };
        sections.push(currentSection);
        return;
      }

      if (!line || line.startsWith("###")) {
        return;
      }

      if (currentSection) {
        currentSection.comments.push(line);
      }
    });

    return sections;
  };

  const fetchMarkdownSections = async (path) => {
    const response = await axios.get(path);
    return parseMarkdownSections(response.data || "");
  };

  const filterGripSections = (sections, tags) => {
    if (!sections?.length) {
      return [];
    }

    const baseNames = ["No mistakes", "Tight ones"];
    const allowed = new Set(baseNames.map((name) => name.toLowerCase()));
    const hasBackhand = tags.some((tag) => tag.includes("backhand"));
    const hasForehand = tags.some((tag) => tag.includes("forehand"));

    if (hasBackhand) {
      allowed.add("shape correction (bh)");
    }

    if (hasForehand) {
      allowed.add("shape correction (fh)");
    }

    if (!hasBackhand && !hasForehand) {
      allowed.add("shape correction (bh)");
      allowed.add("shape correction (fh)");
    }

    return sections.filter((section) => allowed.has((section.name || "").toLowerCase()));
  };

  const formatSectionLabel = (value) =>
    value.replace(/\b([a-z])/g, (match, char) => char.toUpperCase());

  const templateOptions = useMemo(() => {
    const items = [];
    const pushItems = (source, list) => {
      if (!Array.isArray(list)) {
        return;
      }
      list.forEach((section) => {
        if (section?.comments?.length) {
          const key = `${source}|${section.name}`;
          const labelPrefix = source === "grips" ? "Grips" : "Airtoss";
          items.push({
            key,
            label: `${labelPrefix} · ${formatSectionLabel(section.name)}`,
            comments: section.comments,
          });
        }
      });
    };

    pushItems("grips", templateCollections.grips);
    pushItems("airtoss", templateCollections.airtoss);

    return items;
  }, [templateCollections]);

  const selectedTemplate = useMemo(
    () => templateOptions.find((option) => option.key === selectedTemplateGroup) || null,
    [templateOptions, selectedTemplateGroup]
  );

  const templateCommentOptions = useMemo(() => {
    if (!selectedTemplate) {
      return [];
    }
    return selectedTemplate.comments.map((comment, index) => ({
      value: String(index),
      label: comment,
    }));
  }, [selectedTemplate]);

  const shouldShowTemplates = templateOptions.length > 0;

  // Initialize with coachManualFeedback if provided
  useEffect(() => {
    if (coachManualFeedback && coachManualFeedback.trim() !== "") {
      setClosingFeedback(coachManualFeedback);
      setFeedbackStatus("submitted");
    } else {
      // Reset to empty state when coachManualFeedback is empty/null
      setClosingFeedback("");
      setFeedbackStatus("idle");
    }
  }, [coachManualFeedback]);

  // Fetch existing feedback on component mount (only if no coachManualFeedback provided)
  useEffect(() => {
    if (!coachManualFeedback || coachManualFeedback.trim() === "") {
      fetchExistingFeedback();
    }
  }, [coachManualFeedback, postId]); // Add postId dependency to refetch when video changes

  useEffect(() => {
    if (readOnly) {
      setTemplateCollections({ grips: [], airtoss: [] });
      return;
    }

    const hasGripTags = normalizedTags.some((tag) => tag.includes("grip"));
    const hasAirtossTags = normalizedTags.some((tag) => tag.replace(/\s+/g, "").includes("airtoss"));

    if (!hasGripTags && !hasAirtossTags) {
      setTemplateCollections({ grips: [], airtoss: [] });
      setSelectedTemplateGroup("");
      setSelectedTemplateComment("");
      return;
    }

    // Create a cache key based on the tags
    const cacheKey = `${hasGripTags ? 'grip' : ''}_${hasAirtossTags ? 'airtoss' : ''}_${normalizedTags.sort().join('_')}`;

    // Check if we already have cached data for this tag combination
    if (markdownCacheRef.current[cacheKey]) {
      setTemplateCollections(markdownCacheRef.current[cacheKey]);
      // Don't reset selections when using cached data - preserve user's selection
      return;
    }

    let isMounted = true;

    const loadTemplates = async () => {
      setIsTemplateLoading(true);
      try {
        const [gripSections, airtossSections] = await Promise.all([
          hasGripTags ? fetchMarkdownSections("/markdown/GRIPS-v2.md") : Promise.resolve([]),
          hasAirtossTags ? fetchMarkdownSections("/markdown/AIRTOSSES_v2.md") : Promise.resolve([]),
        ]);

        if (!isMounted) {
          return;
        }

        const gripData = hasGripTags ? filterGripSections(gripSections, normalizedTags) : [];

        const newTemplateCollections = {
          grips: gripData,
          airtoss: airtossSections,
        };

        // Cache the result
        markdownCacheRef.current[cacheKey] = newTemplateCollections;

        setTemplateCollections(newTemplateCollections);
        setSelectedTemplateGroup("");
        setSelectedTemplateComment("");
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error loading closing feedback templates:", error);
        }
      } finally {
        if (isMounted) {
          setIsTemplateLoading(false);
        }
      }
    };

    loadTemplates();

    return () => {
      isMounted = false;
    };
  }, [normalizedTags, readOnly]);

  const fetchExistingFeedback = async () => {
    if (!postId || !jwtToken) return;

    try {
      const apiUrl = COACH_MANUAL_FEEDBACK.replace("{postId}", postId);
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200 && response.data?.data) {
        const feedbackContent = response.data.data;
        setClosingFeedback(feedbackContent);
        setFeedbackStatus("submitted");
      }
    } catch (error) {
      console.error("Error fetching coach's closing feedback:", error);
    }
  };

  const handleTemplateGroupChange = (event) => {
    const value = event.target.value;
    setSelectedTemplateGroup(value);
    setSelectedTemplateComment("");
  };

  const handleTemplateCommentChange = (event) => {
    const value = event.target.value;
    setSelectedTemplateComment(value);
    const index = Number(value);
    if (!Number.isNaN(index) && selectedTemplate && selectedTemplate.comments[index]) {
      setClosingFeedback(selectedTemplate.comments[index]);
      if (feedbackStatus === "submitted") {
        setFeedbackStatus("idle");
      }
    }
  };

  const handleTemplateClear = () => {
    setSelectedTemplateGroup("");
    setSelectedTemplateComment("");
  };

  const handleClosingFeedbackSubmit = async () => {
    if (!closingFeedback.trim()) {
      toast.error("Please enter closing feedback before submitting");
      return;
    }

    setFeedbackStatus("submitting");

    try {
      const apiUrl = COACH_MANUAL_FEEDBACK.replace("{postId}", postId);

      const response = await axios.put(
        apiUrl,
        { coachManualFeedback: closingFeedback },
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Closing feedback submitted successfully!");
        setFeedbackStatus("submitted");
      } else {
        throw new Error("Failed to submit feedback");
      }
    } catch (error) {
      console.error("Error submitting closing feedback:", error);
      toast.error("Failed to submit closing feedback. Please try again.");
      setFeedbackStatus("idle");
    }
  };

  // If readOnly is true and no feedback exists, don't render anything
  if (readOnly && (!closingFeedback || closingFeedback.trim() === "")) {
    return null;
  }

  // If readOnly is true and feedback exists, just render the text
  if (readOnly) {
    return (
      <div className="mt-8 p-6 bg-emerald-50/50 rounded-2xl border border-emerald-100 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
        <h3 className="text-lg font-bold text-emerald-800 mb-4 flex items-center gap-2">
          <span className="w-1.5 h-6 bg-emerald-500 rounded-full"></span>
          Coach's Closing Feedback
        </h3>
        <div className="w-full">
          <div className="w-full p-4 min-h-[120px] bg-white border border-emerald-100 rounded-xl text-sm font-medium text-gray-700 whitespace-pre-wrap leading-relaxed shadow-sm">
            {closingFeedback}
          </div>
        </div>
      </div>
    );
  }

  // Regular editable version
  return (
    <div className="mt-8 p-6 bg-white rounded-2xl border border-gray-100 shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-500 relative overflow-hidden group">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-teal-500" />

      <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-3">
        <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
          </svg>
        </div>
        Coach's Closing Feedback
      </h3>

      {isTemplateLoading && (
        <div className="mb-4 text-emerald-600 text-sm font-semibold animate-pulse flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
          Loading templates...
        </div>
      )}
      {!isTemplateLoading && shouldShowTemplates && (
        <div className="mb-6 p-4 bg-emerald-50/50 border border-emerald-100 rounded-xl transition-all hover:bg-emerald-50">
          <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-3 block">Quick templates</span>
          <div className="flex flex-wrap gap-3 items-center">
            <select
              className="flex-1 min-w-[200px] py-2.5 px-3 border border-emerald-200 rounded-lg bg-white text-emerald-800 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm cursor-pointer hover:border-emerald-300"
              value={selectedTemplateGroup}
              onChange={handleTemplateGroupChange}
            >
              <option value="">Select focus area</option>
              {templateOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            {selectedTemplateGroup && (
              <select
                className="flex-[2] min-w-[200px] py-2.5 px-3 border border-emerald-200 rounded-lg bg-white text-emerald-800 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm cursor-pointer hover:border-emerald-300 animate-in fade-in slide-in-from-left-2"
                value={selectedTemplateComment}
                onChange={handleTemplateCommentChange}
              >
                <option value="">Choose a ready comment</option>
                {templateCommentOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            )}
            {(selectedTemplateGroup || selectedTemplateComment) && (
              <button
                type="button"
                className="text-sm font-semibold text-emerald-600 hover:text-emerald-800 underline underline-offset-2 decoration-emerald-200 hover:decoration-emerald-500 transition-all px-2"
                onClick={handleTemplateClear}
              >
                Clear selection
              </button>
            )}
          </div>
        </div>
      )}
      <div className="w-full mb-4 group/textarea relative">
        <textarea
          placeholder="Add your closing feedback and suggestions for the player..."
          className="w-full p-4 min-h-[120px] bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all shadow-inner resize-y placeholder:text-gray-400"
          value={closingFeedback}
          onChange={(e) => setClosingFeedback(e.target.value)}
          disabled={feedbackStatus === "submitted"}
        />
        <div className="absolute inset-0 rounded-xl pointer-events-none ring-1 ring-inset ring-black/5" />
      </div>
      {feedbackStatus === "submitted" && closingFeedback && closingFeedback.trim() !== "" ? (
        <div className="flex justify-end animate-in fade-in zoom-in duration-300">
          <button
            className="px-6 py-2.5 bg-white border border-emerald-200 text-emerald-700 font-bold text-sm rounded-xl hover:bg-emerald-50 hover:border-emerald-300 hover:shadow-md transition-all active:scale-95 flex items-center gap-2"
            onClick={() => setFeedbackStatus("idle")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            Edit Feedback
          </button>
        </div>
      ) : (
        <button
          className={`
            mt-2 px-8 py-3 rounded-xl font-bold text-sm shadow-md transition-all duration-300 flex items-center gap-2 ml-auto
            ${feedbackStatus === "submitting" || !closingFeedback.trim()
              ? 'bg-[#237e45]/50 text-white cursor-not-allowed shadow-none'
              : 'bg-[#237e45] text-white hover:bg-[#1b6136] hover:shadow-lg hover:scale-105 active:scale-95'}
          `}
          onClick={handleClosingFeedbackSubmit}
          disabled={feedbackStatus === "submitting" || !closingFeedback.trim()}
        >
          {feedbackStatus === "submitting" ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Submit Closing Feedback
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default CoachClosingFeedback;
